<template>
	<SessionBlockedForUser v-if="isSessionBlockedForUser" />
	<Session404 v-else-if="is404" @joined="refetchSession" />
	<SessionContainer v-else-if="!isLoading && session" data-test="session-container">
		<template #header-start>
			<ButtonWorkspace />
			<WorkspaceSubscriptionProblemMessage v-if="$workspaceSubscription.problem" />
			<TrialDaysLeft v-else-if="$workspaceSubscription.isTrialPlan" />
			<UpgradeToTeamPlan v-if="$workspaceSubscription.isFreemiumPlan" />
		</template>
		<template #header-middle>
			<SessionPanelLayoutButtons />
		</template>
		<template #header-end>
			<SessionViews
				v-if="session.views && session.views.length"
				:views="session.views"
				class="mt-6 hidden lg:block"
			/>
			<section class="items-center gap-4 ml-12 hidden md:flex">
				<ButtonDelete v-if="canUserEditSession" class="mobile:hidden" @delete="deleteSession(session.id)" />
				<ButtonMoveFromSession
					v-if="canUserEditSession"
					class="text-gray-100"
					tooltipText="Move session"
					titleText="Move to"
					:sessionHasLabels="hasLabels"
					:sessionId="session.id"
					@moved="onMoved"
				/>
				<ButtonInvite v-if="$store.hasUserMemberOrAdminRole" class="mobile:hidden" />
			</section>
			<ButtonCopyLink class="ml-12"> Share </ButtonCopyLink>
			<ButtonGetBrowserExtension v-if="browserInfo.platform.type === 'desktop'" class="hidden lg:block mx-12" />
			<ButtonLogin class="ml-12" />
			<MenuAccount class="mobile:hidden ml-4 flex-none" />
		</template>
		<SessionContent @refetchSession="refetchSession" />
		<SignupFromSessionPopup v-if="isSignupFromSessionPopupVisible" @close="closeSignupFromSessionPopup" />
		<ButtonHelp v-if="!isIntegrationFormOpen" />
		<Transition name="notify">
			<OnboardingNotificationAfterUpload v-if="showOnboardingBanner && canUserEditSession" />
		</Transition>
	</SessionContainer>
</template>

<script>
import * as Sentry from '@sentry/vue'
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'

import queryWithSubscription from 'shared/apollo/queryWithSubscription'
import SessionContainer from 'shared/components/SessionContainer.vue'
import SessionContent from 'shared/components/SessionContent.vue'
import ButtonGetBrowserExtension from 'shared/components/ButtonGetBrowserExtension.vue'
import ButtonDelete from 'shared/components/ButtonDelete.vue'
import ButtonMoveFromSession from 'shared/components/ButtonMoveFromSession.vue'
import ButtonInvite from 'shared/components/ButtonInvite.vue'
import ButtonCopyLink from 'shared/components/ButtonCopyLink.vue'
import ButtonLogin from 'shared/components/ButtonLogin.vue'
import MenuAccount from 'shared/components/MenuAccount.vue'
import ButtonHelp from 'shared/components/ButtonHelp.vue'
import ButtonWorkspace from 'shared/components/ButtonWorkspace.vue'
import SessionViews from 'shared/components/SessionViews.vue'
import TrialDaysLeft from 'shared/components/TrialDaysLeft.vue'
import UpgradeToTeamPlan from 'shared/components/UpgradeToTeamPlan.vue'
import WorkspaceSubscriptionProblemMessage from 'shared/components/WorkspaceSubscriptionProblemMessage.vue'
import querySession from 'shared/graphql/querySession.gql'
import analytics from 'shared/utils/analytics'
import deepMergeObjects from 'shared/utils/deepMergeObjects'
import SessionPanelLayoutButtons from 'shared/components/SessionPanelLayoutButtons.vue'
import {useBrowserInfo} from 'shared/composables/useBrowserInfo'
import {useTitle} from 'shared/composables/useTitle'
import {useDocumentVisibility} from '@vueuse/core'
import {useSessionStore} from 'shared/stores/session'
import {useSessionEventsStore} from 'shared/stores/sessionEvents'
import {useSessionNetworkStore} from 'shared/stores/sessionNetwork'

export default {
	name: 'Session',
	components: {
		SessionContainer,
		SessionContent,
		ButtonGetBrowserExtension,
		ButtonInvite,
		ButtonDelete,
		ButtonLogin,
		MenuAccount,
		ButtonHelp,
		ButtonWorkspace,
		ButtonCopyLink,
		SessionViews,
		ButtonMoveFromSession,
		SessionPanelLayoutButtons,
		SessionBlockedForUser: defineAsyncComponent(() => import('shared/views/SessionBlockedForUser.vue')),
		SignupFromSessionPopup: defineAsyncComponent(() => import('shared/components/SignupFromSessionPopup.vue')),
		Session404: defineAsyncComponent(() => import('shared/views/Session404.vue')),
		OnboardingNotificationAfterUpload: defineAsyncComponent(() =>
			import('shared/components/OnboardingNotificationAfterUpload.vue')
		),
		TrialDaysLeft,
		UpgradeToTeamPlan,
		WorkspaceSubscriptionProblemMessage,
	},
	beforeRouteLeave() {
		if (sessionStorage?.hasUnsavedChanges) {
			const answer = window.confirm(this.unsavedChangesConfirmationMessage)
			if (answer) {
				delete sessionStorage.hasUnsavedChanges
			} else {
				return false
			}
		}
		this.sessionStore.$reset()
	},
	props: {
		sessionId: {type: String, required: true},
		workspaceId: {type: String, default: ''},
		collectionId: {type: String, default: ''},
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {
			title: useTitle(),
			browserInfo: useBrowserInfo(),
			documentVisibility: useDocumentVisibility(),
			session,
			canUserEditSession,
			sessionStore,
			sessionEventsStore: useSessionEventsStore(),
			sessionNetworkStore: useSessionNetworkStore(),
		}
	},
	data() {
		return {
			isLoading: true,
			isSessionBlockedForUser: false,
			is404: false,
			refetchSessionInterval: null,
			unsavedChangesConfirmationMessage: 'The page contains unsaved changes. Do you still want to leave?',
			isSignupFromSessionPopupVisible: false,
			previousRoute: null,
			showOnboardingBanner: this.$route.query.onboarding === 'true',
		}
	},
	apollo: {
		session: queryWithSubscription('querySession', {
			query: querySession,
			variables() {
				return {
					id: this.sessionId,
					userId: this.$auth.userId.value,
					isUserWorkspaceMember: this.$store.isUserWorkspaceMember,
				}
			},
			skip() {
				// This query + subscription is only executed after `getSession()` returned the initial `session`.
				// The reason that a query is used and not just a subscription is that there's no Apollo caches for subscriptions,
				// and the codebase mutates Apollo caches for optimistic updates.
				return this.isLoading || !this.session?.id || this.documentVisibility === 'hidden'
			},
			result({data}) {
				if (!data?.session) return
				this.sessionStore.session = deepMergeObjects(this.session, data.session)
			},
			updateQuery(previousResult, {subscriptionData}) {
				// navigate to session list when session has been deleted by a different client (tab)
				if (previousResult.session && !subscriptionData?.data?.session) {
					if (this.collectionId && this.workspaceId) {
						return this.$routerHelpers.replace({
							name: 'workspace-collection',
							params: {workspaceId: this.workspaceId, collectionId: this.collectionId},
						})
					}
					return this.$routerHelpers.replace({name: 'dashboard'})
				}
			},
		}),
	},
	computed: {
		isUserSessionOwner() {
			return this.session?.ownerId === this.$auth.userId.value
		},
		isDemoSession() {
			return this.session?.uploadMethod === 'demo'
		},
		sessionWorkspaceId() {
			return this.workspaceId || this.session?.workspaceId
		},
		sessionWorkspace() {
			return this.$store.workspaces.find(({workspaceId}) => workspaceId === this.sessionWorkspaceId)
		},
		isSignupCTAsVisible() {
			if (!this.session?.public) return false
			return this.$auth.isAuthenticated.value === false
		},
		hasLabels() {
			return Boolean(this.session?.labels?.length)
		},
		isIntegrationFormOpen() {
			return (
				this.$route.query.jira ||
				this.$route.query.github ||
				this.$route.query.azure ||
				this.$route.query.linear
			)
		},
	},
	watch: {
		'$auth.loading.value': {
			immediate: true,
			handler: function (isLoading) {
				if (isLoading) return
				this.getSession()
			},
		},
		async 'session.events'(events) {
			if (!events) return
			try {
				const data = await fetch(this.session.events).then(res => res.json())
				this.sessionEventsStore.rawEvents = data || []
			} catch (error) {
				Sentry.captureException(error)
				// eslint-disable-next-line no-console
				console.error(error)
			}
		},
		async 'session.networkRequests'(networkRequests) {
			if (!networkRequests) return
			try {
				const data = await fetch(this.session.networkRequests).then(res => res.json())
				this.sessionNetworkStore.rawNetworkRequests = data || {}
			} catch (error) {
				Sentry.captureException(error)
				// eslint-disable-next-line no-console
				console.error(error)
			}
		},
		sessionWorkspace(sessionWorkspace) {
			// this reloads the session if the workspace is deleted in the background
			if (this.isLoading) return
			if (sessionWorkspace) return
			this.$router.go(0)
		},
		canUserEditSession(canEditSession) {
			if (canEditSession) return
			if (this.session?.public) return
			if (this.$store.isUserWorkspaceMember) return
			// reload page
			this.$router.go(0)
		},
		isSignupCTAsVisible(isSignupCTAsVisible) {
			if (!isSignupCTAsVisible) return
			window.addEventListener('playbackPause', this.showSignupFromSessionPopup)
			window.addEventListener('playbackFinished', this.showSignupFromSessionPopup)
		},
	},
	created() {
		if (!sessionStorage) return
		delete sessionStorage.hasUnsavedChanges
	},
	mounted() {
		window.addEventListener('beforeunload', this.confirmTabCloseWithUnsavedChanges)
	},
	unmounted() {
		window.removeEventListener('beforeunload', this.confirmTabCloseWithUnsavedChanges)
		window.removeEventListener('playbackPause', this.showSignupFromSessionPopup)
		window.removeEventListener('playbackFinished', this.showSignupFromSessionPopup)
		clearInterval(this.refetchSessionInterval)
	},
	methods: {
		async getSession() {
			const isInitialLoad = this.session === undefined
			try {
				if (isInitialLoad) this.isLoading = true

				let url = '/sessions/' + this.$route.params.sessionId
				if (!isInitialLoad) url += '?refetch=true'
				const {session, sessionPath} = await this.$api.get(url)

				this.isLoading = false

				if (!session) return

				// sometimes the auth might not have authenticated, but we still have a api-authentication cookie, which allows us to load the session
				// in these cases, we should try to login the user
				if (!session.public && !this.$auth.isAuthenticated.value) {
					return this.$auth.login({redirectAfterLogin: this.$route.fullPath})
				}

				this.sessionStore.session = session
				this.updateSessionDetailViewRoute(sessionPath)
				this.title = session.title ? session.title : `${session.id.substring(0, 4)}... Bug Report`

				// Create an interval to re-fetch the session once every 59min,
				// because the signed Google Cloud Storage file links are valid for 1h,
				// so a user trying to seek to an uncached part of a video after 1h would otherwise run into an issue.
				if (!this.refetchSessionInterval) {
					this.refetchSessionInterval = setInterval(this.getSession, 59 * 60 * 1000)
				}
			} catch (error) {
				if (error.status === 401) {
					this.isLoading = false
					const {isUserWorkspaceMember, hasUserAccess, sessionPath} = error.response
					if (isUserWorkspaceMember && !hasUserAccess) {
						this.isSessionBlockedForUser = true
						this.updateSessionDetailViewRoute(sessionPath)
					}
					return
				}
				if (error.status === 404) {
					this.isLoading = false
					this.is404 = true
					return
				}
				throw error
			} finally {
				if (isInitialLoad) {
					analytics.page('Session', {
						isSessionBlockedForUser: this.isSessionBlockedForUser,
						is404: this.is404,
					})
				}
			}
		},
		showSignupFromSessionPopup() {
			if (!this.isSignupCTAsVisible || localStorage?.hasClosedSignupFromSessionPopup) return
			this.isSignupFromSessionPopupVisible = true
			window.removeEventListener('playbackPause', this.showSignupFromSessionPopup)
			window.removeEventListener('playbackFinished', this.showSignupFromSessionPopup)
		},
		goToMemberSettings() {
			analytics.track('Member Settings Link Clicked', {cta: 'Session View'})
			this.$routerHelpers.navigateToWorkspaceSettings('members')
		},
		async deleteSession(sessionId) {
			const updateSessionsSetAsDeleted = (await import('shared/apollo/updateSessionsSetAsDeleted')).default
			await updateSessionsSetAsDeleted([sessionId], this.$store.collection.id)
			this.$routerHelpers.replace({
				name: 'workspace-collection',
				params: {workspaceId: this.workspaceId, collectionId: this.collectionId},
			})
		},
		async onMoved() {
			await this.refetchSession()
			this.previousRoute = `/workspaces/${this.$store.workspace.id}/collections/${this.$store.collection.id}`
		},
		confirmTabCloseWithUnsavedChanges(event) {
			if (!sessionStorage?.hasUnsavedChanges) return

			event.preventDefault()

			event.returnValue = this.unsavedChangesConfirmationMessage
			return this.unsavedChangesConfirmationMessage
		},
		async updateSessionDetailViewRoute(path) {
			if (!path) return
			if (path === this.$route.path) return
			return this.$routerHelpers.replace({
				path,
				query: this.$route.query,
				hash: this.$route.hash,
			})
		},
		closeSignupFromSessionPopup() {
			this.isSignupFromSessionPopupVisible = false
			if (localStorage) localStorage.hasClosedSignupFromSessionPopup = true
		},
		async refetchSession() {
			await this.$apollo.queries.session.refetch()
		},
	},
}
</script>
